import { Options, optionsBuilder } from '../constants';
import { Planning } from './Planning';
import { useInnovationVideoPlay } from '@/modules/home/hooks/useInnovationVideoPlay';
import { useBreakpoint } from '@/shared/hooks/useBreakpoints';
import { Typography } from '@/shared/ui/typography';
import { useServices } from '@/store/api/services';
import cn from 'classnames';
import { AnimatePresence, Variants, motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import {ComponentType, useMemo, useRef, useState} from 'react';
import dynamic from "next/dynamic";
import OtherBlocks from "@/modules/services/ui/OtherBlocks";
import PlanningV2 from "@/modules/services/ui/PlanningV2";

const Catalog: ComponentType<any> = dynamic(() =>
    import('./Catalog').then((mod) => mod.Catalog), {
    ssr: false,
  }
);

export const OurServices = () => {
  const { services } = useServices();
  const [currentOpened, setCurrentOpened] = useState<number[]>([]);
  const { isDesktop, isLaptop, isMobile } = useBreakpoint();
  const [currentSlide, setCurrentSlide] = useState(0);
  const ref = useRef<HTMLVideoElement | null>(null);
  const { playFullScreen } = useInnovationVideoPlay(ref);
  const { i18n } = useTranslation('common');

  const onClickTitle = (index: number) => {
    if (index === 2) {
      window.open('https://rulogfreight.ru/', '_blank');
    } else {
      currentOpened.includes(index)
        ? setCurrentOpened((prev) => prev.filter((current) => current !== index))
        : setCurrentOpened([...currentOpened, index]);
    }
  }

  const options = useMemo(
    () => optionsBuilder(services as Options),
    [services],
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.7, delay: 1 }}
      viewport={{ once: true }}
      className="mx-auto my-0 max-w-[1440px]"
    >
      {options.map((option, index) => (
        <motion.div
          className={cn(
            'mb:py-8 tb:py-10 dk:py-14 hover:bg-gray-5 duration-300 ease-linear',
            {
              'bg-gray-5': currentOpened.includes(index),
              'bg-dark/0': !currentOpened.includes(index),
              'dk:mt-[-10px]': index === 5 && currentOpened.includes(index),
            },
          )}
          key={index}
        >
          <div className={cn('container')}>
            <div>
              <Typography
                variant="titleX"
                className={cn(
                  'tb:max-dk:text-titleL mb:max-tb:text-titleL dk:max-w-[696px] tb:max-w-[575px] cursor-pointer hover:text-black',
                  {
                    'text-black': currentOpened.includes(index),
                    'text-gray-6': !currentOpened.includes(index),
                  },
                  option.titleWidth,
                )}
                onClick={() => onClickTitle(index)}
              >
                <motion.span>{option.title}</motion.span>
              </Typography>
              {index === 0 && (
                <AnimatePresence>
                      {currentOpened.includes(0) &&
                        <Catalog
                          manager={option.manager}
                          contacts={option.contacts}
                          presentation={option.presentation}
                        />
                      }

                </AnimatePresence>
              )}
              {/* Первый блок */}
              {index === 1 && (
                <AnimatePresence>
                  {currentOpened.includes(1) && (
                    <div className="relative">
                      <motion.div
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                          open: {
                            opacity: 1,
                            height: 'auto',
                          },
                          collapsed: {
                            opacity: 0,
                            height: 0,
                          },
                        }}
                        transition={{
                          delay: 0.1,
                          duration: 0.3,
                          easy: 'linear',
                        }}
                        className="flex flex-col overflow-hidden"
                      >
                        <Typography
                          variant="textX"
                          className={cn(
                            'dk:mt-[24px] tb:mt-[15px] mb:mt-[12px] tb:mb-[38px] dk:mb-[50px] text-gray-1 mb:max-tb:text-textS dk:max-w-[857px] tb:max-w-[100%] mb:max-w-[343px] mb-9',
                            {
                              'dk:mb-0': option.image,
                            },
                            `${option.textSize}`,
                          )}
                        >
                          {option.description}
                        </Typography>

                        <ul className="tabs mb:max-tb:overflow-x-auto mb:max-tb:mb-[39px] mb:max-tb:gap-[19px] mb:max-tb:max-w-[100%] mb-[38px] ml-[3px] flex gap-[40px]">
                          {option.textSubtitles &&
                            option.textSubtitles.map((item, index) => (
                              <li
                                key={item}
                                onClick={() => setCurrentSlide(index)}
                                className="cursor-pointer"
                              >
                                <Typography
                                  variant="textX"
                                  className={cn(
                                    'tb:hover:text-dark whitespace-nowrap',
                                    {
                                      'text-dark': currentSlide === index,
                                      'text-gray-2': currentSlide !== index,
                                      'mb:max-tb:w-[190px]': index === 1,
                                      'mb:max-tb:w-[247px]': index === 3,
                                    },
                                  )}
                                >
                                  {item}
                                </Typography>
                              </li>
                            ))}
                        </ul>
                        <AnimatePresence mode="wait">
                          {option.subtitles && (
                            <motion.div
                              className="flex flex-col"
                              key={option.subtitles[currentSlide].title}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div className="flex items-center gap-x-[15px]">
                                <div className="relative h-[35px] w-[35px]">
                                  <Image
                                    src={option.subtitles[currentSlide].icon}
                                    alt="advantage"
                                    fill
                                  />
                                </div>
                                <Typography
                                  variant="titleM"
                                  className="text-gray-7"
                                >
                                  {option.subtitles[currentSlide].title}
                                </Typography>
                              </div>
                              <>
                                {option.subtitles[currentSlide].list && (
                                  <ul className="mb:max-dk:flex-col mb:max-tb:gap-y-[24px] tb:max-dk:gap-[23px] ml-[30px] mt-[25px] flex w-auto gap-x-[66px]">
                                    {/* @ts-ignore */}
                                    {option.subtitles[currentSlide].list.map(
                                      (listItem) => (
                                        <li
                                          className="mb:max-w-[317px] tb:max-w-[830px] dk:max-w-[341px] flex flex-col gap-[12px] "
                                          key={listItem.title}
                                        >
                                          <Typography
                                            variant="titleY"
                                            className={cn(
                                              'listItem before:bg-gray-1 text-gray-7',
                                              listItem.titleWidth,
                                            )}
                                          >
                                            {listItem.title}
                                          </Typography>
                                          <Typography
                                            variant="textM"
                                            className="mb:max-tb:text-textS text-gray-1 ml-[18px]"
                                          >
                                            {listItem.description}
                                          </Typography>
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                )}
                              </>
                              {option.subtitles[currentSlide].description && (
                                <Typography
                                  variant="textM"
                                  className="mb:max-tb:text-textS dk:mt-[6px] tb:mt-[6px] mb:mt-[10px] mb:ml-[51px] text-gray-1 dk:max-w-[735px]"
                                >
                                  {option.subtitles[currentSlide].description}
                                </Typography>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    </div>
                  )}
                </AnimatePresence>
              )}
              {index === 3 && (
                <PlanningV2
                  currentOpened={currentOpened}
                  option={option}
                  index={index}
                  isDesktop={isDesktop}
                  isLaptop={isLaptop}
                />
              )}
              {index !== 2 && index !== 1 && index !== 3 && (
                // @ts-ignore
                <OtherBlocks
                  currentOpened={currentOpened}
                  option={option}
                  index={index}
                  isDesktop={isDesktop}
                  isLaptop={isLaptop}
                />
              )}

            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};
